<!--
  Patient media prototype (Media DB / Viewer story). Every layout reads one shared mock through
  @/api/mediaApi, which keeps writes in this browser (localStorage + IndexedDB): no media reaches
  keyon yet. Switching layout never changes the data or the step you are on.

  Layouts: ?v=A|B|C. Each is the whole experience (browse, search, open a set, compare, Edit set,
  capture, notes and messages), shown a different way: A as pages with back links, B as a list with
  a panel beside it, C as a full-screen viewer over the library. The steps are in the URL (mediaNav).
  The switcher strip and the variant badge stay on while this page is under review, so feedback is
  tied to the layout it was about; both go, with the losing layouts, once one is chosen.
  Opened from a patient chart (the Media tile), the page opens that patient's media: their name,
  date of birth and gender come from the chart session, and the mock adds them to its roster.
  "View as" is prototype only: it shows what the patient John Doe would see (patient-visible items
  only, no editing).
-->
<template>
  <div class="md-proto">
    <div class="md-proto-top">
      <div class="md-proto-who">
        <!-- Before init the patient is still null, which would read as the personal library. -->
        <h1>{{ !store.ready ? 'Media' : store.inLibrary ? 'My library' : patientTitle }}</h1>
        <span v-if="patientFacts" class="md-muted">{{ patientFacts }}</span>
      </div>
      <label v-if="store.ready && store.canManage" class="md-proto-picker">
        <span class="md-label">Patient</span>
        <select class="md-select" :value="store.patientUid || ''" @change="pickPatient($event.target.value)">
          <option v-for="p in store.patients" :key="p.unique_ID" :value="p.unique_ID">{{ p.name }}</option>
          <option value="">My library (not in any chart)</option>
        </select>
      </label>
    </div>

    <div class="md-proto-devbar">
      <span>Layout</span>
      <button v-for="k in KEYS" :key="k" :class="{ on: variant === k }" @click="setVariant(k)">{{ k }} · {{ VNAMES[k] }}</button>
      <span>View as</span>
      <button :class="{ on: store.viewer.kind === 'caregiver' }" @click="viewAs('caregiver')">Caregiver</button>
      <button :class="{ on: store.viewer.kind === 'patient' }" @click="viewAs('patient')">Patient (John Doe)</button>
      <button class="reset" @click="resetData">Reset local data</button>
    </div>
    <div class="md-row md-proto-badgerow">
      <span class="md-proto-badge" data-cy="variant-badge">{{ variant }} · {{ VNAMES[variant] }}</span>
      <span class="md-small md-muted">Prototype: media stays in this browser. Seed pictures are illustrations, not patient photos.</span>
    </div>
    <div v-if="store.viewer.kind === 'patient'" class="md-banner info">
      Viewing as the patient John Doe: only items a caregiver marked “The patient can see this” appear, and nothing can be edited.
    </div>

    <p v-if="!store.ready" class="md-muted">Loading…</p>
    <component :is="LAYOUTS[variant]" v-else :key="`${store.viewer.kind}:${store.patientUid}`" :nav="nav" />

    <div v-if="store.toast" class="md-toast" :class="store.toast.tone" role="status">{{ store.toast.text }}</div>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { isViewingPatientChart } from '@/data/getters';
import { getSessionPatientPersonalId } from '@/utils/directivesSession';
import { useMediaStore } from '@/stores/mediaStore';
import { useMediaNav } from './mediaNav';
import MediaLayoutPages from './MediaLayoutPages.vue';
import MediaLayoutSplit from './MediaLayoutSplit.vue';
import MediaLayoutViewer from './MediaLayoutViewer.vue';
import './media-proto.css';

const route = useRoute();
const router = useRouter();
const store = useMediaStore();
const vuex = useStore();
const nav = useMediaNav();

const VNAMES = { A: 'Pages', B: 'Split view', C: 'Viewer first' };
const KEYS = ['A', 'B', 'C'];
const LAYOUTS = { A: MediaLayoutPages, B: MediaLayoutSplit, C: MediaLayoutViewer };
const variant = computed(() => (KEYS.includes(route.query.v) ? route.query.v : 'A'));
function setVariant(v) { router.replace({ query: { ...route.query, v } }); }

const patientTitle = computed(() => (store.patient ? `${store.patient.name} · Media` : 'Media'));
const patientFacts = computed(() => {
  const p = store.patient;
  if (!p) return '';
  return [p.date_of_birth && `DOB ${p.date_of_birth}`, p.gender].filter(Boolean).join(' · ');
});

// The open chart's patient (allData.patient while a chart is open), shaped like the mock's patients
// rows. Outside a chart there is none and the page opens the first sample patient.
function chartPatient() {
  const allData = vuex.getters['login/getAllData'];
  if (!isViewingPatientChart(allData)) return null;
  const p = allData.patient || {};
  const uid = getSessionPatientPersonalId(p);
  if (!uid) return null;
  return {
    unique_ID: uid,
    first_name: p.firstName || '',
    lastname: p.lastName || '',
    date_of_birth: p.dob || p.dateOfBirth || p.date_of_birth || '',
    // pacmny-be sends gender as a list (["Male"]).
    gender: [].concat(p.gender || []).join(', '),
  };
}

// Another patient, another viewer or fresh data: start again from that library.
function pickPatient(uid) {
  store.setPatient(uid || null);
  router.replace({ query: { v: route.query.v, patient: uid || 'library' } });
}
async function viewAs(kind) {
  await store.setViewer(kind);
  router.replace({ query: { v: route.query.v, patient: store.patientUid || undefined } });
}
async function resetData() {
  await store.reset();
  nav.library();
}

onMounted(async () => {
  const q = route.query.patient;
  const chart = chartPatient();
  await store.init(q && q !== 'library' ? q : chart && chart.unique_ID, chart);
  if (q === 'library' && store.canManage) store.setPatient(null);
});
</script>

<style scoped>
.md-proto-top { display:flex; align-items:flex-end; gap:16px; flex-wrap:wrap; margin:4px 0 14px; }
.md-proto-who { flex:1 1 auto; }
.md-proto-who h1 { font-size:28px; color:var(--teal); }
.md-proto-picker { display:flex; flex-direction:column; gap:4px; min-width:240px; }
.md-proto-devbar { display:flex; flex-wrap:wrap; align-items:center; gap:6px; background:#111a1c; color:#cfd8da; border-radius:6px; padding:8px 10px; margin-bottom:8px; font-size:13px; }
.md-proto-devbar span { font-weight:700; text-transform:uppercase; letter-spacing:.05em; font-size:11px; margin:0 4px 0 6px; }
.md-proto-devbar button { background:#2b3a3d; color:#fff; border:none; border-radius:4px; padding:5px 10px; font-size:13px; cursor:pointer; }
.md-proto-devbar button.on { background:#7fe0d6; color:#0d2a2c; font-weight:700; }
.md-proto-devbar button.reset { margin-left:auto; background:#5a2b2b; }
.md-proto-badgerow { margin-bottom:14px; }
.md-proto-badge { padding:4px 10px; border-radius:6px; background:#eef4f4; color:#3a464c; font-size:13px; font-weight:600; }
</style>
